import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, Plus, Search, Clock, User, Trash2,
  Edit2, X, Check, MapPin, Phone, Loader2, ChevronLeft, ChevronRight,
  Filter, List, Grid3X3, AlertCircle, CheckCircle, XCircle, Play
} from "lucide-react";

// Status configuration
const STATUSES = {
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-700", dotColor: "bg-blue-500" },
  confirmed: { label: "Confirmed", color: "bg-emerald-100 text-emerald-700", dotColor: "bg-emerald-500" },
  in_progress: { label: "In Progress", color: "bg-purple-100 text-purple-700", dotColor: "bg-purple-500" },
  completed: { label: "Completed", color: "bg-slate-100 text-slate-600", dotColor: "bg-slate-400" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-600", dotColor: "bg-red-400" }
};

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

// Format time for display
const formatTime = (timeString) => {
  if (!timeString) return "";
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Check if date is today
const isToday = (dateString) => {
  if (!dateString) return false;
  const today = new Date().toISOString().split('T')[0];
  return dateString === today;
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = STATUSES[status] || STATUSES.scheduled;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dotColor}`} />
      {config.label}
    </span>
  );
};

// Appointment Card Component
const AppointmentCard = ({ appointment, onEdit, onStatusChange, onDelete }) => {
  const today = isToday(appointment.date);
  
  return (
    <div 
      className={`p-4 bg-white rounded-lg border transition-all hover:shadow-md ${
        today ? "border-[#d4a853] bg-amber-50/30" : "border-slate-200"
      }`}
      data-testid={`appointment-${appointment.id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-bold text-[#1e3a5f]">
              {formatTime(appointment.start_time)}
              {appointment.end_time && ` - ${formatTime(appointment.end_time)}`}
            </span>
            {today && (
              <span className="text-xs bg-[#d4a853] text-[#1e3a5f] px-2 py-0.5 rounded-full font-semibold">
                Today
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500">{formatDate(appointment.date)}</p>
        </div>
        <StatusBadge status={appointment.status} />
      </div>
      
      <div className="space-y-2 mb-3">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-slate-400" />
          <span className="font-medium text-slate-700">{appointment.customer_name}</span>
          {appointment.customer_phone && (
            <a href={`tel:${appointment.customer_phone}`} className="text-slate-400 hover:text-[#1e3a5f]">
              <Phone className="w-4 h-4" />
            </a>
          )}
        </div>
        
        <p className="text-sm text-slate-600 ml-6">{appointment.service_description}</p>
        
        {appointment.address && (
          <div className="flex items-start gap-2 text-sm text-slate-500">
            <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{appointment.address}</span>
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
        {appointment.status === "scheduled" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onStatusChange(appointment.id, "confirmed")}
            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            Confirm
          </Button>
        )}
        {(appointment.status === "scheduled" || appointment.status === "confirmed") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onStatusChange(appointment.id, "in_progress")}
            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
          >
            <Play className="w-4 h-4 mr-1" />
            Start
          </Button>
        )}
        {appointment.status === "in_progress" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onStatusChange(appointment.id, "completed")}
            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
          >
            <Check className="w-4 h-4 mr-1" />
            Complete
          </Button>
        )}
        {appointment.status !== "completed" && appointment.status !== "cancelled" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(appointment)}
            className="text-slate-600"
          >
            <Edit2 className="w-4 h-4 mr-1" />
            Edit
          </Button>
        )}
        {appointment.status !== "cancelled" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onStatusChange(appointment.id, "cancelled")}
            className="text-red-500 hover:text-red-600 hover:bg-red-50 ml-auto"
          >
            <XCircle className="w-4 h-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(appointment)}
          className="text-slate-400 hover:text-red-500"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Calendar View Component
const CalendarView = ({ month, year, appointmentsByDate, selectedDate, onSelectDate, onPrevMonth, onNextMonth }) => {
  const monthNames = ["January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  
  // Get first day of month and number of days
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  // Get today's date string
  const todayStr = new Date().toISOString().split('T')[0];
  
  // Generate calendar days
  const days = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
    days.push({
      day: i,
      date: dateStr,
      appointments: appointmentsByDate[dateStr] || [],
      isToday: dateStr === todayStr,
      isSelected: dateStr === selectedDate
    });
  }
  
  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={onPrevMonth}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <CardTitle className="text-lg text-[#1e3a5f]">
            {monthNames[month]} {year}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onNextMonth}>
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Day names */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(day => (
            <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((dayData, index) => (
            <button
              key={dayData ? dayData.date : `empty-${index}`}
              onClick={() => dayData && onSelectDate(dayData.date)}
              disabled={!dayData}
              className={`aspect-square p-1 rounded-lg text-sm relative transition-colors ${
                !dayData ? "invisible" : ""
              } ${
                dayData?.isSelected 
                  ? "bg-[#1e3a5f] text-white" 
                  : dayData?.isToday 
                    ? "bg-[#d4a853]/20 text-[#1e3a5f] font-bold" 
                    : "hover:bg-slate-100"
              }`}
              data-testid={dayData ? `calendar-day-${dayData.date}` : undefined}
            >
              {dayData && (
                <>
                  <span>{dayData.day}</span>
                  {dayData.appointments.length > 0 && (
                    <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                      {dayData.appointments.slice(0, 3).map((apt, i) => (
                        <span 
                          key={i} 
                          className={`w-1.5 h-1.5 rounded-full ${
                            STATUSES[apt.status]?.dotColor || "bg-blue-500"
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Appointment Form Modal
const AppointmentFormModal = ({ appointment, clients, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    customer_name: appointment?.customer_name || "",
    customer_phone: appointment?.customer_phone || "",
    service_description: appointment?.service_description || "",
    date: appointment?.date || new Date().toISOString().split('T')[0],
    start_time: appointment?.start_time || "09:00",
    end_time: appointment?.end_time || "",
    address: appointment?.address || "",
    notes: appointment?.notes || "",
    status: appointment?.status || "scheduled"
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    if (!formData.service_description.trim()) {
      toast.error("Service description is required");
      return;
    }
    
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="appointment-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#1e3a5f]">
            {appointment ? "Edit Appointment" : "New Appointment"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Customer Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Customer Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              placeholder="Customer name"
              className="border-slate-200"
              list="clients-list"
              data-testid="appointment-customer-name"
            />
            <datalist id="clients-list">
              {clients.map(c => (
                <option key={c.id} value={c.full_name} />
              ))}
            </datalist>
          </div>
          
          {/* Customer Phone */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Customer Phone</label>
            <Input
              type="tel"
              value={formData.customer_phone}
              onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
              placeholder="(555) 555-5555"
              className="border-slate-200"
              data-testid="appointment-customer-phone"
            />
          </div>
          
          {/* Service Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Service Description <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.service_description}
              onChange={(e) => setFormData({ ...formData, service_description: e.target.value })}
              placeholder="e.g., Kitchen deep cleaning"
              className="border-slate-200"
              data-testid="appointment-service"
            />
          </div>
          
          {/* Date & Time */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="border-slate-200"
                data-testid="appointment-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Time</label>
              <Input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="border-slate-200"
                data-testid="appointment-start-time"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">End Time</label>
              <Input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="border-slate-200"
                data-testid="appointment-end-time"
              />
            </div>
          </div>
          
          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Address / Location</label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="123 Main St, City, NJ"
              className="border-slate-200"
              data-testid="appointment-address"
            />
          </div>
          
          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#d4a853]/50"
              data-testid="appointment-status-select"
            >
              <option value="scheduled">Scheduled</option>
              <option value="confirmed">Confirmed</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
              className="border-slate-200 resize-none"
              rows={2}
              data-testid="appointment-notes"
            />
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-[#1e3a5f] hover:bg-[#152a45]"
              data-testid="appointment-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {appointment ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Empty State
const EmptyState = ({ onAddAppointment }) => (
  <div className="text-center py-16">
    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
      <CalendarIcon className="w-8 h-8 text-slate-400" />
    </div>
    <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">No appointments scheduled</h3>
    <p className="text-slate-500 mb-4 max-w-sm mx-auto">
      Add your first appointment to start managing your schedule.
    </p>
    <Button onClick={onAddAppointment} className="bg-[#d4a853] hover:bg-[#b8922d] text-[#1e3a5f]">
      <Plus className="w-4 h-4 mr-2" />
      Add First Appointment
    </Button>
  </div>
);

// Main Component
export default function ProAppointmentsPage() {
  const { token } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list"); // list, calendar
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  
  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [appointmentsByDate, setAppointmentsByDate] = useState({});

  // Fetch appointments
  const fetchAppointments = useCallback(async () => {
    if (!token) return;
    
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (searchQuery) params.append("search", searchQuery);
      
      const response = await axios.get(`${API}/pro/appointments?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointments(response.data.appointments || []);
      setTodayAppointments(response.data.today || []);
    } catch (error) {
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, searchQuery]);

  // Fetch calendar data
  const fetchCalendarData = useCallback(async () => {
    if (!token || viewMode !== "calendar") return;
    
    try {
      const response = await axios.get(
        `${API}/pro/appointments/calendar?month=${calendarMonth + 1}&year=${calendarYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAppointmentsByDate(response.data.appointments_by_date || {});
    } catch (error) {
      console.error("Failed to load calendar data");
    }
  }, [token, calendarMonth, calendarYear, viewMode]);

  // Fetch clients for dropdown
  const fetchClients = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/pro/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || []);
    } catch (error) {
      // Silently fail
    }
  }, [token]);

  useEffect(() => {
    fetchAppointments();
    fetchClients();
  }, [fetchAppointments, fetchClients]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  // Create appointment
  const handleCreate = async (data) => {
    try {
      await axios.post(`${API}/pro/appointments`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Appointment created");
      fetchAppointments();
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create appointment");
      throw error;
    }
  };

  // Update appointment
  const handleUpdate = async (data) => {
    if (!editingAppointment) return;
    
    try {
      await axios.put(`${API}/pro/appointments/${editingAppointment.id}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Appointment updated");
      fetchAppointments();
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update appointment");
      throw error;
    }
  };

  // Update status
  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await axios.put(`${API}/pro/appointments/${appointmentId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Appointment ${newStatus === "cancelled" ? "cancelled" : `marked as ${newStatus}`}`);
      fetchAppointments();
      fetchCalendarData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Delete appointment
  const handleDelete = async (appointment) => {
    if (!window.confirm(`Delete this appointment with ${appointment.customer_name}?`)) return;
    
    try {
      await axios.delete(`${API}/pro/appointments/${appointment.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Appointment deleted");
      fetchAppointments();
      fetchCalendarData();
    } catch (error) {
      toast.error("Failed to delete appointment");
    }
  };

  // Handlers
  const handleEdit = (appointment) => {
    setEditingAppointment(appointment);
    setShowModal(true);
  };

  const handleAddNew = () => {
    setEditingAppointment(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAppointment(null);
  };

  // Calendar navigation
  const handlePrevMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  };

  // Get appointments for selected date in calendar
  const selectedDateAppointments = selectedDate 
    ? appointments.filter(a => a.date === selectedDate)
    : [];

  // Stats
  const stats = {
    total: appointments.length,
    today: todayAppointments.length,
    upcoming: appointments.filter(a => a.status === "scheduled" || a.status === "confirmed").length,
    completed: appointments.filter(a => a.status === "completed").length
  };

  return (
    <div className="space-y-6" data-testid="pro-appointments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Appointments</h1>
          <p className="text-slate-500">Manage your schedule and appointments</p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded ${viewMode === "list" ? "bg-white shadow" : ""}`}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("calendar")}
              className={`p-2 rounded ${viewMode === "calendar" ? "bg-white shadow" : ""}`}
              data-testid="view-calendar-btn"
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
          </div>
          
          <Button
            onClick={handleAddNew}
            className="bg-[#d4a853] hover:bg-[#b8922d] text-[#1e3a5f]"
            data-testid="add-appointment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Appointment
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#1e3a5f]">{stats.total}</p>
            <p className="text-xs text-slate-500">Total</p>
          </CardContent>
        </Card>
        <Card className={`border-slate-200 ${stats.today > 0 ? "bg-amber-50 border-[#d4a853]" : ""}`}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#d4a853]">{stats.today}</p>
            <p className="text-xs text-slate-500">Today</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.upcoming}</p>
            <p className="text-xs text-slate-500">Upcoming</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
            <p className="text-xs text-slate-500">Completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Today's Appointments */}
      {todayAppointments.length > 0 && viewMode === "list" && (
        <Card className="border-[#d4a853] bg-amber-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#d4a853]" />
              Today's Appointments ({todayAppointments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {todayAppointments.map((appointment) => (
              <AppointmentCard
                key={appointment.id}
                appointment={appointment}
                onEdit={handleEdit}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Filters (List View Only) */}
      {viewMode === "list" && (
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by customer or service..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-slate-200"
                  data-testid="appointments-search"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#d4a853]/50"
                  data-testid="appointments-status-filter"
                >
                  <option value="all">All Status</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#d4a853] mx-auto" />
        </div>
      ) : viewMode === "calendar" ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <CalendarView
              month={calendarMonth}
              year={calendarYear}
              appointmentsByDate={appointmentsByDate}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
            />
          </div>
          
          {/* Selected Day Appointments */}
          <div>
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-[#1e3a5f]">
                  {selectedDate ? formatDate(selectedDate) : "Select a date"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!selectedDate ? (
                  <p className="text-slate-500 text-sm py-4 text-center">
                    Click on a date to see appointments
                  </p>
                ) : selectedDateAppointments.length === 0 ? (
                  <p className="text-slate-500 text-sm py-4 text-center">
                    No appointments on this date
                  </p>
                ) : (
                  <div className="space-y-3">
                    {selectedDateAppointments.map((appointment) => (
                      <AppointmentCard
                        key={appointment.id}
                        appointment={appointment}
                        onEdit={handleEdit}
                        onStatusChange={handleStatusChange}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      ) : appointments.length === 0 && !searchQuery && statusFilter === "all" ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <EmptyState onAddAppointment={handleAddNew} />
          </CardContent>
        </Card>
      ) : appointments.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center">
            <p className="text-slate-500">No appointments match your filters</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#1e3a5f]">
              All Appointments ({appointments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {appointments
              .filter(a => !isToday(a.date) || a.status === "completed" || a.status === "cancelled")
              .map((appointment) => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  onEdit={handleEdit}
                  onStatusChange={handleStatusChange}
                  onDelete={handleDelete}
                />
              ))}
          </CardContent>
        </Card>
      )}

      {/* Form Modal */}
      {showModal && (
        <AppointmentFormModal
          appointment={editingAppointment}
          clients={clients}
          onSave={editingAppointment ? handleUpdate : handleCreate}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
